import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, type Variants, type Transition } from 'framer-motion';

type ToastProps = {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  variant?: 'success' | 'error' | 'info';
  title: string;
  description?: string;
  placement?: 'top-center' | 'center' | 'bottom-right';
  autoCloseMs?: number;
};

const THEMES = {
  success: { bg: 'var(--c-teal-weak)',    color: 'var(--c-deep-teal)', ring: 'var(--c-kivo-teal)' },
  error:   { bg: 'rgba(244,168,142,0.18)', color: 'var(--c-charcoal)', ring: 'var(--c-coral)' },
  info:    { bg: 'var(--c-charcoal-06)',  color: 'var(--ink)',          ring: 'var(--c-charcoal-30)' },
} as const;

export default function Toast({
  open,
  onOpenChange,
  variant = 'info',
  title,
  description,
  placement = 'center',
  autoCloseMs = 0,
}: ToastProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    if (!open || !autoCloseMs) return;
    const id = setTimeout(() => onOpenChange?.(false), autoCloseMs);
    return () => { clearTimeout(id); };
  }, [open, autoCloseMs, onOpenChange]);

  const theme = THEMES[variant];

  const springTx = {
    type: 'spring',
    stiffness: 420,
    damping: 32,
    mass: 0.8,
  } satisfies Transition;

  const topVariants = {
    hidden: { opacity: 0, y: -14, scale: 0.98 },
    show: { opacity: 1, y: 0, scale: 1, transition: springTx },
    exit: { opacity: 0, y: -10, scale: 0.98, transition: { duration: 0.18 } satisfies Transition },
  } satisfies Variants;

  const bottomVariants = {
    hidden: { opacity: 0, y: 14, scale: 0.98 },
    show: { opacity: 1, y: 0, scale: 1, transition: springTx },
    exit: { opacity: 0, y: 10, scale: 0.98, transition: { duration: 0.18 } satisfies Transition },
  } satisfies Variants;

  const centerVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    show: { opacity: 1, scale: 1, transition: springTx },
    exit: { opacity: 0, scale: 0.96, transition: { duration: 0.2 } satisfies Transition },
  } satisfies Variants;

  const variants = placement === 'center'
    ? centerVariants
    : placement === 'top-center'
      ? topVariants
      : bottomVariants;

  const wrapperCls =
    placement === 'center'
      ? 'fixed inset-0 z-[200] flex items-center justify-center p-4 pointer-events-none'
      : placement === 'top-center'
        ? 'fixed inset-x-0 top-[max(1rem,env(safe-area-inset-top))] z-[200] flex justify-center px-4 pointer-events-none'
        : 'fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-4 z-[200] px-4 pointer-events-none';

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className={wrapperCls}>
          <motion.div
            key="toast"
            variants={variants}
            initial="hidden"
            animate="show"
            exit="exit"
            className="relative w-full max-w-sm p-3 pr-9 pointer-events-auto overflow-hidden"
            style={{
              background: 'var(--c-cream)',
              borderRadius: 'var(--r-lg)',
              boxShadow: 'var(--sh-soft)',
              border: `1px solid ${theme.ring}`,
              color: theme.color,
            }}
            role="status"
            aria-live="polite"
          >
            {/* Variant tint layer */}
            <div
              className="pointer-events-none absolute inset-0"
              style={{ background: theme.bg, borderRadius: 'inherit' }}
              aria-hidden
            />

            {/* Progress bar */}
            {autoCloseMs > 0 && (
              <div className="pointer-events-none absolute left-3 right-3 top-0" aria-hidden>
                <motion.div
                  key={autoCloseMs}
                  className="h-0.5 rounded-full"
                  style={{ background: theme.ring, transformOrigin: 'left center' }}
                  initial={{ scaleX: 1, opacity: 0.9 }}
                  animate={{ scaleX: 0, opacity: 0.9 }}
                  transition={{ duration: autoCloseMs / 1000, ease: 'linear' }}
                />
              </div>
            )}

            <div className="relative pl-8">
              <div className="absolute left-0 top-0" style={{ color: theme.ring }} aria-hidden>
                <svg viewBox="0 0 24 24" className="h-5 w-5">
                  {variant === 'success' && <path d="M5 13l4 4L19 7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
                  {variant === 'error'   && <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" fill="none" stroke="currentColor" strokeWidth="2" />}
                  {variant === 'info'    && <path d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" fill="none" stroke="currentColor" strokeWidth="2" />}
                </svg>
              </div>
              <div className="relative font-medium" style={{ fontSize: 'var(--fs-body)' }}>{title}</div>
              {description && (
                <div className="relative mt-0.5" style={{ fontSize: 'var(--fs-small)', color: 'var(--ink-muted)' }}>{description}</div>
              )}
            </div>

            <button
              aria-label="Close"
              onClick={() => onOpenChange?.(false)}
              className="absolute right-2 top-2 rounded-md p-1"
              style={{ color: 'var(--ink-muted)' }}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4">
                <path d="M18 6L6 18M6 6l12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
